import { marked } from "marked";
import { isVisibleAt, normalizePrivacy, parseFrontmatter } from "@vortex-os/core";
import type { Privacy } from "@vortex-os/core";
import { stripPrivateSections } from "./filter.js";
import { applyTemplate, DEFAULT_TEMPLATE } from "./template.js";
import type { RenderOptions, RenderResult } from "./types.js";

/**
 * Render a markdown source to HTML, applying document-level and
 * section-level privacy filtering before conversion.
 *
 * If the document's own privacy exceeds the target, an empty result is
 * returned with a `document-not-visible` warning and no body conversion
 * is performed.
 */
export async function renderHtml(
  source: string,
  options: RenderOptions,
): Promise<RenderResult> {
  const { frontmatter, body } = parseFrontmatter<{
    privacy?: unknown;
    title?: unknown;
  }>(source);
  const documentPrivacy: Privacy = normalizePrivacy(frontmatter.privacy);

  if (!isVisibleAt(documentPrivacy, options.targetPrivacy)) {
    return {
      html: "",
      documentPrivacy,
      visible: false,
      warnings: [
        {
          kind: "document-not-visible",
          reason: `Document privacy is ${documentPrivacy}; viewer target is ${options.targetPrivacy}.`,
        },
      ],
    };
  }

  const stripped = stripPrivateSections(body, options.targetPrivacy);
  const inner = (await Promise.resolve(
    marked.parse(stripped.content, { gfm: true, breaks: false }),
  )) as string;
  const title =
    options.title ?? (typeof frontmatter.title === "string" ? frontmatter.title : "");
  const template = options.template ?? DEFAULT_TEMPLATE;
  const html = applyTemplate(template, { title, content: inner });

  return {
    html,
    documentPrivacy,
    visible: true,
    warnings: stripped.warnings,
  };
}
